/**
 * Écran Projets — la relecture de ce qui a été déposé.
 *
 * Son absence a produit la phrase la plus coûteuse reçue jusqu'ici : **« je me suis
 * reconnecté, je n'ai rien trouvé »**. Les mesures étaient en base depuis le premier
 * dépôt ; rien ne les affichait. Un outil de maîtrise des procédés dont on ne retrouve
 * pas les mesures le lendemain est une démonstration, pas un outil.
 *
 * Trois partis pris.
 *
 * **La liste montre ce qui vit, pas ce qui existe.** Un projet se juge à ses mesures et à
 * la date de son dernier dépôt, pas à son identifiant ni à sa date de création. Les plus
 * récemment alimentés viennent en tête : c'est là que se passe quelque chose.
 *
 * **Les cartes sont recalculées, pas mises en cache côté navigateur.** Elles viennent du
 * même moteur que celles du wizard, sur les mesures persistées. Deux chemins d'affichage
 * pour une même carte finiraient par diverger, et la divergence tomberait sur le chiffre
 * qui décide d'une livraison.
 *
 * **L'historique des dépôts est visible.** C'est là qu'on lit qu'un format a changé — la
 * version du mapping saute de 1 à 2 — sans avoir à ouvrir les journaux du serveur.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  FileSpreadsheet,
  FolderOpen,
  Layers,
  Ruler,
  Table2,
} from "lucide-react";

import { CharacteristicsTable, buildRows } from "./CharacteristicsTable";
import { DiagnosisPanel } from "./DiagnosisPanel";
import { PreviewChart } from "./PreviewChart";
import { WindowPicker } from "./WindowPicker";
import { DEFAULT_WINDOW, type AnalysisWindow } from "./mappingApi";
import {
  DEPOSIT_STATUS,
  frenchDate,
  getProject,
  getProjectSeries,
  listProjects,
  type Project,
  type ProjectDetail,
  type ProjectSeries,
} from "./projectsApi";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Skeleton,
  Td,
  Th,
  cn,
  index,
  num,
} from "./ui/primitives";

// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------

function ProjectCard({
  project,
  onOpen,
}: {
  project: Project;
  onOpen: (id: string) => void;
}) {
  const vide = project.measurements_count === 0;

  return (
    <button
      onClick={() => onOpen(project.id)}
      className={cn(
        "group w-full rounded-[var(--radius-card)] border border-line bg-surface p-4 text-left",
        "transition-colors hover:border-line-strong hover:bg-surface-2",
      )}
    >
      <div className="flex items-start gap-2.5">
        <FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[0.92rem] font-semibold tracking-tight">
            {project.name}
          </div>
          <div className="mt-0.5 text-[0.75rem] text-faint">
            {vide
              ? "Aucun dépôt — déposez un export pour commencer la série"
              : `Dernier dépôt le ${frenchDate(project.last_deposit_at)}`}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
        <span className="text-[0.78rem]">
          <span className="tnum font-semibold">
            {num(project.measurements_count)}
          </span>{" "}
          <span className="text-faint">mesures</span>
        </span>
        <span className="text-[0.78rem]">
          <span className="tnum font-semibold">
            {num(project.characteristics_count)}
          </span>{" "}
          <span className="text-faint">caractéristiques</span>
        </span>
        <span className="text-[0.78rem]">
          <span className="tnum font-semibold">{num(project.files_count)}</span>{" "}
          <span className="text-faint">dépôts</span>
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Détail
// ---------------------------------------------------------------------------

function DepositsTable({ detail }: { detail: ProjectDetail }) {
  // Une version de mapping qui change au fil de l'historique, c'est un format machine
  // qui a bougé. Le signaler ici évite d'avoir à l'expliquer : la colonne se lit seule.
  const versions = new Set(
    detail.deposits.map((d) => d.template_version).filter((v) => v !== null),
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <FileSpreadsheet className="h-4 w-4 text-muted" />
            Historique des dépôts
          </span>
        }
        hint={
          versions.size > 1
            ? `${versions.size} versions de mapping — le format de cette machine a changé en cours de route.`
            : "Chaque fichier reçu, et ce qu'il a apporté à la série."
        }
        right={<Badge tone="neutral">{num(detail.deposits.length)}</Badge>}
      />
      <div className="max-h-80 overflow-auto">
        <table className="w-full border-collapse text-[0.8rem]">
          <thead className="sticky top-0 bg-surface">
            <tr>
              <Th>Fichier</Th>
              <Th>Reçu</Th>
              <Th className="text-right">Mesures</Th>
              <Th className="text-right">Écartées</Th>
              <Th>État</Th>
              <Th>Mapping</Th>
            </tr>
          </thead>
          <tbody>
            {detail.deposits.map((d) => (
              <tr key={d.id}>
                <Td className="max-w-[16rem] truncate font-mono text-[0.74rem]">
                  {d.filename}
                </Td>
                <Td className="tnum whitespace-nowrap text-muted">
                  {frenchDate(d.received_at)}
                </Td>
                <Td className="tnum text-right font-medium">
                  {num(d.measurements_written)}
                </Td>
                <Td
                  className={cn(
                    "tnum text-right",
                    d.rows_rejected > 0 ? "text-warn" : "text-faint",
                  )}
                >
                  {d.rows_rejected === 0 ? "—" : num(d.rows_rejected)}
                </Td>
                <Td>
                  <Badge tone={d.status === "done" ? "good" : "warn"}>
                    {DEPOSIT_STATUS[d.status] ?? d.status}
                  </Badge>
                </Td>
                <Td className="tnum text-faint">
                  {d.template_version ? `v${d.template_version}` : "—"}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SpecsTable({ detail }: { detail: ProjectDetail }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <Ruler className="h-4 w-4 text-muted" />
            Caractéristiques du projet
          </span>
        }
        hint="Tolérances en vigueur aujourd'hui, telles qu'elles ont été enregistrées."
      />
      <table className="w-full border-collapse text-[0.8rem]">
        <thead>
          <tr>
            <Th>Code</Th>
            <Th className="text-right">Mesures</Th>
            <Th>Période couverte</Th>
            <Th className="text-right">Mini</Th>
            <Th className="text-right">Cible</Th>
            <Th className="text-right">Maxi</Th>
          </tr>
        </thead>
        <tbody>
          {detail.characteristics.map((c) => (
            <tr key={c.id}>
              <Td className="font-mono text-[0.76rem] font-medium">{c.code}</Td>
              <Td className="tnum text-right">{num(c.measurements_count)}</Td>
              <Td className="tnum whitespace-nowrap text-muted">
                {c.first_ts ? (
                  <>
                    {frenchDate(c.first_ts)} → {frenchDate(c.last_ts)}
                  </>
                ) : (
                  <span className="text-faint">—</span>
                )}
              </Td>
              <Td className="tnum text-right">{index(c.lsl)}</Td>
              <Td className="tnum text-right text-muted">{index(c.target)}</Td>
              <Td className="tnum text-right">{index(c.usl)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      {detail.characteristics.every(
        (c) => c.lsl === null && c.usl === null,
      ) && (
        <p className="border-t border-line px-4 py-2.5 text-[0.76rem] text-faint">
          Aucune tolérance enregistrée : les cartes de contrôle restent
          calculables, mais Cp, Cpk, Pp et Ppk ne le sont pas. Ce sont des
          mesures de conformité à un plan, pas des propriétés du fichier.
        </p>
      )}
    </Card>
  );
}

function ProjectDetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [series, setSeries] = useState<ProjectSeries | null>(null);
  const [window, setWindow] = useState<AnalysisWindow>(DEFAULT_WINDOW);
  const [selected, setSelected] = useState<string | null>(null);
  const [natures, setNatures] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let annulé = false;
    setBusy(true);
    getProject(id)
      .then((d) => {
        if (!annulé) setDetail(d);
      })
      .catch((e: unknown) => {
        if (!annulé)
          setError(e instanceof Error ? e.message : "erreur inconnue");
      })
      .finally(() => {
        if (!annulé) setBusy(false);
      });
    return () => {
      annulé = true;
    };
  }, [id]);

  const charger = useCallback(
    (w: AnalysisWindow, n: Record<string, string>) => {
      setBusy(true);
      getProjectSeries(id, w, undefined, n)
        .then((s) => {
          setSeries(s);
          setSelected((courant) =>
            courant && s.charts.some((c) => c.characteristic_code === courant)
              ? courant
              : (s.charts[0]?.characteristic_code ?? null),
          );
        })
        .catch((e: unknown) =>
          setError(e instanceof Error ? e.message : "erreur inconnue"),
        )
        .finally(() => setBusy(false));
    },
    [id],
  );

  useEffect(() => {
    charger(DEFAULT_WINDOW, {});
  }, [charger]);

  const rows = useMemo(
    () => (series ? buildRows(series.charts) : []),
    [series],
  );
  const chart = useMemo(
    () =>
      series?.charts.find((c) => c.characteristic_code === selected) ??
      series?.charts[0] ??
      null,
    [series, selected],
  );

  const changeWindow = (next: AnalysisWindow) => {
    setWindow(next);
    charger(next, natures);
  };

  const changeNature = (code: string, nature: string) => {
    const next = { ...natures, [code]: nature };
    setNatures(next);
    charger(window, next);
  };

  if (error) {
    return (
      <div className="space-y-3">
        <Button onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Tous les projets
        </Button>
        <Card className="border-bad/40 bg-bad-soft p-4 text-[0.85rem] text-bad">
          {error}
        </Card>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Tous les projets
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold tracking-tight">
          {detail.project.name}
        </h1>
        <Badge tone="neutral">
          {num(detail.project.measurements_count)} mesures accumulées
        </Badge>
      </div>

      <div
        className={cn(
          busy && "pointer-events-none opacity-60 transition-opacity",
        )}
      >
        {rows.length > 0 && (
          <Card className="overflow-hidden">
            <div className="border-b border-line bg-surface-2/60">
              <WindowPicker
                window={window}
                total={Math.max(
                  ...(series?.charts.map((c) => c.series_total) ?? [1]),
                  1,
                )}
                hasTimestamps
                label={series?.charts[0]?.window_label ?? ""}
                onChange={changeWindow}
              />
            </div>
            <CardHeader
              title={
                <span className="flex items-center gap-1.5">
                  <Table2 className="h-4 w-4 text-muted" />
                  Vue d'ensemble
                </span>
              }
              hint="Trié par instabilité. Cliquer une ligne trace sa carte de contrôle."
              right={
                <Badge tone="neutral">{rows.length} caractéristiques</Badge>
              }
            />
            <CharacteristicsTable
              rows={rows}
              selected={selected}
              onSelect={setSelected}
            />
          </Card>
        )}

        {series?.reason && rows.length === 0 && (
          <Card className="p-4 text-[0.85rem] text-muted">{series.reason}</Card>
        )}

        {chart && (
          <Card className="mt-3 overflow-hidden">
            <CardHeader
              title={
                <span className="flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-muted" />
                  <span className="font-mono">{chart.characteristic_code}</span>
                </span>
              }
              hint={`Carte ${chart.chart_type} · ${num(chart.points_total)} points · ${
                chart.window_label
              }`}
              right={
                chart.violations_total > 0 ? (
                  <Badge tone="bad">
                    {num(chart.violations_total)} en violation
                  </Badge>
                ) : (
                  <Badge tone="good">sous contrôle</Badge>
                )
              }
            />
            <div className="px-2 pt-2">
              <PreviewChart chart={chart} />
            </div>
            {chart.diagnosis && (
              <DiagnosisPanel
                diagnosis={chart.diagnosis}
                code={chart.characteristic_code}
                onNatureChange={changeNature}
              />
            )}
          </Card>
        )}
      </div>

      <SpecsTable detail={detail} />
      <DepositsTable detail={detail} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

export function ProjectsView() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    if (opened) return;
    listProjects()
      .then(setProjects)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "erreur inconnue"),
      );
  }, [opened]);

  if (opened) {
    return <ProjectDetailView id={opened} onBack={() => setOpened(null)} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Projets</h1>
        <p className="mt-1 max-w-2xl text-[0.88rem] text-muted">
          Un projet, c'est une machine ou un contrôle que vous suivez dans le
          temps. Les dépôts successifs s'y accumulent dans la même série, même
          si le format des fichiers change en cours de route.
        </p>
      </div>

      {error && (
        <Card className="border-bad/40 bg-bad-soft p-4 text-[0.85rem] text-bad">
          {error}
        </Card>
      )}

      {!projects && !error && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      )}

      {projects?.length === 0 && (
        <Card className="p-6 text-center">
          <FolderOpen className="mx-auto h-8 w-8 text-faint" />
          <div className="mt-2 text-[0.9rem] font-medium">Aucun projet</div>
          <p className="mx-auto mt-1 max-w-md text-[0.82rem] text-muted">
            Déposez un export machine depuis « Analyser un fichier » : vous
            nommerez le projet à ce moment-là, et les dépôts suivants du même
            format s'y rangeront sans rien demander.
          </p>
        </Card>
      )}

      {projects && projects.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onOpen={setOpened} />
          ))}
        </div>
      )}
    </div>
  );
}
